import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";

const FEATURED_TOOLS = [
  {
    id: "chat",
    title: "Competitor Chat",
    description: "Ask questions about pricing, features, hiring, and positioning using live competitor context.",
    icon: "ai-chat-01",
    href: "/chat",
    badge: "Live",
    badgeVariant: "success" as const,
    features: ["Pricing", "Features", "Positioning", "Hiring"],
  },
  {
    id: "scrape",
    title: "Web Scraper",
    description: "Pull structured data from any page and turn it into clean, usable intelligence.",
    icon: "magic-wand-01",
    href: "/scrape",
    badge: "Live",
    badgeVariant: "success" as const,
    features: ["Markdown", "Links", "JSON", "Crawl"],
  },
  {
    id: "snapshots",
    title: "Visual Snapshots",
    description: "Capture competitor pages across desktop, tablet, and mobile in one run.",
    icon: "camera-01",
    href: "/snapshots",
    badge: "Live",
    badgeVariant: "success" as const,
    features: ["Desktop", "Tablet", "Mobile", "History"],
  },
  {
    id: "alerts",
    title: "Change Alerts",
    description: "Watch key pages and get notified when meaningful updates happen.",
    icon: "notification-01",
    href: "/alerts",
    badge: "Live",
    badgeVariant: "success" as const,
    features: ["Monitoring", "AI scoring", "Alerts", "Digest"],
  },
];

const QUICK_ACTIONS = [
  { label: "Chat with a competitor", icon: "ai-chat-01", href: "/chat" },
  { label: "Scrape a page", icon: "magic-wand-01", href: "/scrape" },
  { label: "View snapshots", icon: "camera-01", href: "/snapshots" },
  { label: "Check alerts", icon: "notification-01", href: "/alerts" },
];

const HIGHLIGHTS = [
  { label: "Live tools", value: "4" },
  { label: "Monitor signals", value: "24/7" },
  { label: "AI-assisted", value: "Always" },
];

const MORE_TOOLS = [
  { title: "CMO Intelligence", status: "Coming soon", href: "/cmo", icon: "target-01" },
  { title: "Scraping Agents", status: "Coming soon", href: "/agents", icon: "cpu-01" },
];

export function IntelligencePage() {
  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Intelligence"
        description="Monitor competitors, track changes, and turn market signals into action."
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl space-y-8 px-6 py-8">
          <section className="grid gap-3 md:grid-cols-3">
            {HIGHLIGHTS.map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-border bg-card p-4 shadow-sm"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {item.label}
                </p>
                <p className="mt-3 text-2xl font-semibold tracking-tight">{item.value}</p>
              </div>
            ))}
          </section>

          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Quick start
              </p>
              <Link to="/chat" className="font-mono text-[11px] text-foreground underline-offset-4 hover:underline">
                Open workspace
              </Link>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {QUICK_ACTIONS.map((action) => (
                <Link
                  key={action.href}
                  to={action.href}
                  className="group flex items-center gap-3 rounded-xl border border-border bg-muted/20 px-3 py-3 transition-colors hover:border-muted-foreground hover:bg-muted/30"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background">
                    <Icon name={action.icon} size={14} className="text-muted-foreground group-hover:text-foreground" />
                  </div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground">{action.label}</span>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Core tools
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              {FEATURED_TOOLS.map((tool) => (
                <Link key={tool.id} to={tool.href} className="block h-full">
                  <Card className="h-full transition-colors hover:border-muted-foreground">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-muted/30">
                            <Icon name={tool.icon} size={16} className="text-foreground" />
                          </div>
                          <div>
                            <CardTitle className="text-sm">{tool.title}</CardTitle>
                          </div>
                        </div>
                        <Badge variant={tool.badgeVariant}>{tool.badge}</Badge>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <p className="text-sm leading-relaxed text-muted-foreground">{tool.description}</p>

                      <div className="flex flex-wrap gap-2">
                        {tool.features.map((feature) => (
                          <span
                            key={feature}
                            className="rounded-lg border border-border bg-muted/20 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.12em] text-foreground">
                        Open tool
                        <Icon name="arrow-right-01" size={10} className="ml-0.5" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                More intelligence
              </p>
              <Link to="/email-intelligence" className="font-mono text-[11px] text-muted-foreground hover:text-foreground">
                Email Intel
              </Link>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {MORE_TOOLS.map((tool) => (
                <div
                  key={tool.title}
                  className="flex items-center justify-between rounded-xl border border-dashed border-border bg-muted/10 px-4 py-3 opacity-75"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background">
                      <Icon name={tool.icon} size={14} className="text-muted-foreground" />
                    </div>
                    <span className="text-sm text-muted-foreground">{tool.title}</span>
                  </div>
                  <Badge variant="secondary">{tool.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
